import * as React from "react";

import {
    MarketResearchSummaryHeader,
    MarketResearchType,
    useMarketResearchContext,
    useMarketResearchSummaryStyles,
} from "..";
import {
    currencyWholeDollarFormatter,
    percentCompactFormatter,
    SummaryRating,
    Tile,
    TilesSection,
} from "../../..";
import { ErrorIcon, SuccessIcon, WarningIcon } from "../../../assets/icons";

interface MarketResearchSummaryProps {
    marketResearchType: MarketResearchType;
    isJobInsights: boolean;
}

export const MarketResearchSummary = (props: MarketResearchSummaryProps) => {
    const { marketResearchType, isJobInsights } = props;
    const classes = useMarketResearchSummaryStyles({});

    const statusTile = {
        root: classes.statusTile,
        title: classes.statusTitle,
        content: classes.statusContent,
        paper: classes.tilePaper,
    };

    const {
        summary: {
            isBusy: summaryIsBusy,
            data: summaryData,
            error: summaryError,
        },
    } = useMarketResearchContext();

    const displayStat = (
        value: number | undefined | null,
        formattedValue: string
    ) => {
        if (value === undefined || value === null) return "n/a";
        return formattedValue;
    };

    const getTitleAdornment = (rating: SummaryRating) => {
        if (!!summaryError) return <ErrorIcon />;
        if (!rating) return null;
        if (rating.isNegative()) return <WarningIcon />;
        if (rating.isPositive()) return <SuccessIcon />;
        if (rating.isNotAvailable()) return <ErrorIcon />;
        return null;
    };

    const getTooltip = (tooltip: string) => {
        if (!!summaryError) return summaryError.friendlyMessages;
        return tooltip;
    };

    return (
        <div className={classes.root}>
            <MarketResearchSummaryHeader
                marketResearchType={marketResearchType}
            />
            <TilesSection
                usePaper={false}
                classes={{ paper: classes.tilesSectionPaper }}
                className={classes.tilesSection}
            >
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="hiring difficulty"
                    title="hiring difficulty"
                    subTitle="out of 10"
                    tooltip={getTooltip(
                        summaryData?.hiringDifficulty?.rating.getHiringDifficultySummaryMessage()
                    )}
                    isBusy={summaryIsBusy}
                    titleAdornment={getTitleAdornment(
                        summaryData?.hiringDifficulty?.rating
                    )}
                >
                    {displayStat(
                        summaryData?.hiringDifficulty?.score,
                        summaryData?.hiringDifficulty?.score?.toString()
                    )}
                </Tile>
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="jobTitleMatch"
                    title="job title match"
                    subTitle="confidence"
                    tooltip={getTooltip(
                        summaryData?.jobTitle?.rating.getJobTitleSummaryMessage()
                    )}
                    isBusy={summaryIsBusy}
                    titleAdornment={getTitleAdornment(
                        summaryData?.jobTitle?.rating
                    )}
                >
                    {displayStat(
                        summaryData?.jobTitle?.confidencePercentage,
                        percentCompactFormatter.format(
                            summaryData?.jobTitle?.confidencePercentage
                        )
                    )}
                </Tile>
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="medianSalary"
                    title="median salary"
                    titleAdornment={getTitleAdornment(
                        summaryData?.compensation?.rating
                    )}
                    tooltip={
                        isJobInsights
                            ? getTooltip(
                                  summaryData?.compensation?.rating.getCompensationSummaryMessage()
                              )
                            : null
                    }
                    isBusy={summaryIsBusy}
                >
                    {displayStat(
                        summaryData?.compensation?.medianSalaryInUSDollars,
                        currencyWholeDollarFormatter.format(
                            summaryData?.compensation?.medianSalaryInUSDollars
                        )
                    )}
                </Tile>
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="supply"
                    title="supply"
                    subTitle="available profiles"
                    tooltip={getTooltip(
                        summaryData?.supplyDemand?.rating.getSupplySummaryMessage()
                    )}
                    isBusy={summaryIsBusy}
                    titleAdornment={getTitleAdornment(
                        summaryData?.supplyDemand?.rating
                    )}
                >
                    {displayStat(
                        summaryData?.supplyDemand?.supplyCount,
                        summaryData?.supplyDemand?.supplyCount?.toLocaleString()
                    )}
                </Tile>
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="demand"
                    title="demand"
                    subTitle="job postings"
                    tooltip={getTooltip(
                        summaryData?.supplyDemand?.rating.getDemandSummaryMessage()
                    )}
                    isBusy={summaryIsBusy}
                    titleAdornment={getTitleAdornment(
                        summaryData?.supplyDemand?.rating
                    )}
                >
                    {displayStat(
                        summaryData?.supplyDemand?.demandCount,
                        summaryData?.supplyDemand?.demandCount?.toLocaleString()
                    )}
                </Tile>
                <Tile
                    small
                    useSubTitle
                    classes={statusTile}
                    id="diversity"
                    title="diversity"
                    isBusy={summaryIsBusy}
                >
                    {displayStat(
                        summaryData?.diversity?.regionalPercent,
                        `${summaryData?.diversity?.regionalPercent} %`
                    )}
                </Tile>
            </TilesSection>
        </div>
    );
};
